<template>
  <div class="admin-support">
    <div v-if="!hasSupportAccess" class="access-denied-card">
      <div class="access-denied-icon"><i class="fa-solid fa-lock"></i></div>
      <h3>Access restricted</h3>
      <p>Only super admin or support admins can view customer support tickets from the website form.</p>
      <p class="access-denied-hint">Sign in with an account that has the support or super admin role.</p>
    </div>

    <div v-else class="admin-support__inner">
      <div class="page-header">
        <h1>Customer support tickets</h1>
        <p>
          Requests submitted from the public Support page (
          <code>/ipromiseafrica/support</code>
          ). Resolve tickets when your team has handled them.
        </p>
      </div>

      <div v-if="loadError" class="load-error-banner" role="alert">
        <i class="fa-solid fa-triangle-exclamation"></i>
        {{ loadError }}
      </div>

      <!-- Filters -->
      <div class="support-filters">
        <input
          v-model="searchQuery"
          type="search"
          placeholder="Search name, email, topic, or message…"
          autocomplete="off"
        />

        <select v-model="statusFilter" aria-label="Filter by status">
          <option value="">All statuses</option>
          <option value="Pending">Pending</option>
          <option value="Resolved">Resolved</option>
        </select>

        <button type="button" class="btn-refresh" :disabled="loading" @click="fetchTickets">
          <i class="fa-solid fa-rotate" :class="{ 'fa-spin': loading }"></i>
          Refresh
        </button>
      </div>

      <div v-if="loading && tickets.length === 0" class="loading-state">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Loading tickets…</span>
      </div>

      <!-- Support Table -->
      <div v-else class="support-table">
        <table>
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Customer</th>
              <th>Email</th>
              <th>Topic</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="filteredTickets.length === 0">
              <td colspan="6" class="empty-cell">
                {{ loading ? "…" : "No tickets match your filters." }}
              </td>
            </tr>
            <tr
              v-for="ticket in filteredTickets"
              :key="ticket.ticket_id"
              :class="{ resolved: isResolved(ticket) }"
            >
              <td>{{ ticket.ticket_id }}</td>
              <td>{{ ticket.name }}</td>
              <td>
                <a :href="'mailto:' + ticket.email" class="email-link">{{ ticket.email }}</a>
              </td>
              <td>{{ ticket.issue_type }}</td>

              <td>
                <span class="status" :class="statusClass(ticket)">
                  {{ ticket.status }}
                </span>
              </td>

              <td>
                <div class="ticket-actions">
                  <button
                    type="button"
                    class="action-btn view"
                    title="View details"
                    @click="viewTicket(ticket)"
                  >
                    <i class="fa-solid fa-eye"></i>
                  </button>

                  <button
                    v-if="!isResolved(ticket)"
                    type="button"
                    class="action-btn resolve"
                    title="Mark resolved"
                    :disabled="resolvingId === ticket.ticket_id"
                    @click="markResolved(ticket)"
                  >
                    <i
                      class="fa-solid"
                      :class="resolvingId === ticket.ticket_id ? 'fa-spinner fa-spin' : 'fa-check-circle'"
                    ></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Ticket Modal -->
    <div v-if="selectedTicket" class="ticket-modal" @click.self="closeModal">
      <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="ticket-modal-title">
        <h2 id="ticket-modal-title">Ticket {{ selectedTicket.ticket_id }}</h2>

        <p><strong>Name:</strong> {{ selectedTicket.name }}</p>
        <p>
          <strong>Email:</strong>
          <a :href="'mailto:' + selectedTicket.email">{{ selectedTicket.email }}</a>
        </p>
        <p><strong>Topic:</strong> {{ selectedTicket.issue_type }}</p>
        <p class="full-message">
          <strong>Message:</strong>
        </p>
        <pre class="message-body">{{ selectedTicket.message }}</pre>

        <p>
          <strong>Submitted:</strong>
          {{ formatDate(selectedTicket.created_at) }}
        </p>
        <p>
          <strong>Status:</strong>
          <span class="status" :class="statusClass(selectedTicket)">{{ selectedTicket.status }}</span>
        </p>

        <div class="modal-actions">
          <button v-if="!isResolved(selectedTicket)" type="button" class="btn-resolve" @click="markResolved(selectedTicket)">
            Mark resolved
          </button>
          <button type="button" class="close" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAdminAuthStore } from "../../authen/adminAuth";
import { buildApiUrl } from "@/config/api";

export default {
  name: "AdminSupport",

  inject: {
    setSupportTicketPendingCount: {
      from: "setSupportTicketPendingCount",
      default: null,
    },
  },

  data() {
    return {
      tickets: [],
      searchQuery: "",
      statusFilter: "",
      selectedTicket: null,
      loading: false,
      loadError: "",
      resolvingId: null,
    };
  },

  computed: {
    hasSupportAccess() {
      const store = useAdminAuthStore();
      return store.canAccessSupportAdminTools;
    },

    filteredTickets() {
      const q = this.searchQuery.trim().toLowerCase();
      return this.tickets.filter((t) => {
        const matchesSearch =
          !q ||
          (t.name && t.name.toLowerCase().includes(q)) ||
          (t.email && t.email.toLowerCase().includes(q)) ||
          (t.issue_type && t.issue_type.toLowerCase().includes(q)) ||
          (t.message && t.message.toLowerCase().includes(q));

        const st = (t.status || "").trim();
        const matchesStatus =
          !this.statusFilter ||
          st.toLowerCase() === this.statusFilter.toLowerCase();

        return matchesSearch && matchesStatus;
      });
    },
  },

  mounted() {
    const store = useAdminAuthStore();
    store.checkToken();
    if (!this.hasSupportAccess) return;
    this.fetchTickets();
  },

  methods: {
    getAdminHeaders(json = false) {
      const store = useAdminAuthStore();
      store.checkToken();
      const headers = { Accept: "application/json" };
      if (json) headers["Content-Type"] = "application/json";
      const token = store.accessToken;
      if (token) headers["Authorization"] = `Bearer ${token}`;
      return headers;
    },

    normalizeTicket(raw) {
      if (!raw || typeof raw !== "object") return null;
      const st = (raw.status || "").trim().toLowerCase();
      const status = st === "resolved" ? "Resolved" : "Pending";
      return {
        ticket_id: raw.ticket_id,
        id: raw.id,
        name: raw.name,
        email: raw.email,
        issue_type: raw.issue_type,
        message: raw.message,
        created_at: raw.created_at,
        status,
      };
    },

    isResolved(ticket) {
      return (ticket.status || "").toLowerCase() === "resolved";
    },

    statusClass(ticket) {
      return this.isResolved(ticket) ? "resolved" : "pending";
    },

    formatDate(iso) {
      if (!iso) return "—";
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return String(iso);
      }
    },

    notifyPendingCount() {
      if (typeof this.setSupportTicketPendingCount !== "function") return;
      const pending = this.tickets.filter((t) => !this.isResolved(t)).length;
      this.setSupportTicketPendingCount(pending);
    },

    async fetchTickets() {
      if (!this.hasSupportAccess) return;
      this.loading = true;
      this.loadError = "";
      try {
        const url = buildApiUrl("/admin/support-tickets/");
        const res = await fetch(url, { headers: this.getAdminHeaders(false) });

        if (res.status === 401) {
          this.loadError = "Your admin session expired. Please sign in again.";
          window.dispatchEvent(
            new CustomEvent("show-toast", {
              detail: {
                title: "Session expired",
                message: "Sign in again to load support tickets.",
                type: "warning",
              },
            })
          );
          return;
        }

        if (res.status === 403) {
          this.loadError = "You do not have permission to view customer tickets.";
          return;
        }

        if (!res.ok) {
          const errText = await res.text();
          throw new Error(errText || `Request failed (${res.status})`);
        }

        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        this.tickets = list.map((row) => this.normalizeTicket(row)).filter(Boolean);
        this.notifyPendingCount();
      } catch (e) {
        console.error("Failed to load tickets:", e);
        this.loadError = "Could not load tickets. Check your connection and API configuration.";
        window.dispatchEvent(
          new CustomEvent("show-toast", {
            detail: {
              title: "Load failed",
              message: this.loadError,
              type: "error",
            },
          })
        );
      } finally {
        this.loading = false;
      }
    },

    viewTicket(ticket) {
      this.selectedTicket = ticket;
    },

    closeModal() {
      this.selectedTicket = null;
    },

    async markResolved(ticket) {
      if (!this.hasSupportAccess || !ticket || this.isResolved(ticket)) return;
      this.resolvingId = ticket.ticket_id;
      try {
        const url = buildApiUrl(`/admin/support-tickets/${ticket.ticket_id}/resolve`);
        const res = await fetch(url, {
          method: "PATCH",
          headers: this.getAdminHeaders(false),
        });

        if (res.status === 401) {
          window.dispatchEvent(
            new CustomEvent("show-toast", {
              detail: {
                title: "Session expired",
                message: "Sign in again to update tickets.",
                type: "warning",
              },
            })
          );
          return;
        }

        if (res.status === 403) {
          window.dispatchEvent(
            new CustomEvent("show-toast", {
              detail: {
                title: "Access denied",
                message: "Only super admin or support can resolve tickets.",
                type: "error",
              },
            })
          );
          return;
        }

        if (!res.ok) {
          let detail = "Could not update ticket.";
          try {
            const body = await res.json();
            if (body && body.detail) detail = typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail);
          } catch {
            /* ignore */
          }
          throw new Error(detail);
        }

        const updated = await res.json();
        const normalized = this.normalizeTicket(updated);
        const idx = this.tickets.findIndex((t) => t.ticket_id === ticket.ticket_id);
        if (idx !== -1 && normalized) this.tickets.splice(idx, 1, normalized);

        if (this.selectedTicket && this.selectedTicket.ticket_id === ticket.ticket_id && normalized) {
          this.selectedTicket = normalized;
        }

        window.dispatchEvent(
          new CustomEvent("show-toast", {
            detail: {
              title: "Ticket resolved",
              message: `Ticket #${ticket.ticket_id} marked as resolved.`,
              type: "success",
            },
          })
        );
      } catch (err) {
        console.error("Resolve ticket error:", err);
        window.dispatchEvent(
          new CustomEvent("show-toast", {
            detail: {
              title: "Update failed",
              message: err.message || "Could not mark ticket as resolved.",
              type: "error",
            },
          })
        );
      } finally {
        this.resolvingId = null;
        this.notifyPendingCount();
      }
    },
  },
};
</script>

<style scoped>
.admin-support {
  padding: 1.5rem;
  background-color: var(--background-color1);
  color: var(--text-color1);
  box-sizing: border-box;
}

.access-denied-card {
  max-width: 520px;
  margin: 2rem auto;
  padding: 2rem;
  text-align: center;
  background: var(--background-color2);
  border: 1px solid var(--background-color3);
  border-radius: 16px;
}

.access-denied-icon {
  font-size: 2.5rem;
  color: var(--border-color-hover);
  margin-bottom: 1rem;
}

.access-denied-card h3 {
  margin-bottom: 0.75rem;
}

.access-denied-card p {
  color: var(--text-color2);
  line-height: 1.5;
}

.access-denied-hint {
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.page-header h1 {
  font-size: 1.5rem;
  margin-bottom: 0.35rem;
}

.page-header p {
  color: var(--text-color2);
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 1.25rem;
}

.page-header code {
  font-size: 0.85em;
  padding: 0.1rem 0.35rem;
  border-radius: 4px;
  background: var(--background-color3);
}

.load-error-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  border-radius: 8px;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.35);
  color: #fecaca;
  font-size: 0.9rem;
}

.support-filters {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.3rem;
  flex-wrap: wrap;
  align-items: center;
}

.support-filters input,
.support-filters select {
  flex: 1 1 180px;
  min-width: 140px;
  background: var(--background-color3);
  border: 1px solid var(--button-border-color);
  color: var(--text-color1);
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
}

.btn-refresh {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  background: var(--background-color3);
  border: 1px solid var(--background-color3);
  color: var(--text-color1);
  font-weight: 600;
}

.btn-refresh:hover:not(:disabled) {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-state {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 2rem;
  justify-content: center;
  color: var(--text-color2);
}

.support-table {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid var(--background-color3);
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

th,
td {
  padding: 0.85rem 0.75rem;
  border-bottom: 1px solid var(--background-color3);
  text-align: left;
}

th {
  background: var(--background-color2);
  font-weight: 700;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-color2);
}

tr.resolved td {
  opacity: 0.85;
}

.empty-cell {
  text-align: center;
  color: var(--text-color2);
  padding: 2rem !important;
}

.email-link {
  color: var(--border-color-hover);
  text-decoration: none;
}

.email-link:hover {
  text-decoration: underline;
}

/* Status Badges */
.status {
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 6px;
  text-transform: capitalize;
  font-weight: 600;
  font-size: 0.75rem;
}

.status.pending {
  background: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
}

.status.resolved {
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
}

.ticket-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease;
  background: transparent;
}

.action-btn.view {
  color: #60a5fa;
}

.action-btn.resolve {
  color: #34d399;
}

.action-btn:hover:not(:disabled) {
  transform: scale(1.08);
  background: var(--background-color3);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal */
.ticket-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  padding: 1rem;
}

.modal-content {
  background: var(--background-color2);
  color: var(--text-color1);
  padding: 1.8rem;
  border-radius: 12px;
  width: 92%;
  max-width: 520px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 0 14px var(--box-shadow);
  animation: popIn 0.25s ease;
}

.modal-content h2 {
  margin-top: 0;
  margin-bottom: 1rem;
}

.full-message {
  margin-bottom: 0.35rem;
}

.message-body {
  margin: 0 0 1rem;
  padding: 0.85rem;
  border-radius: 8px;
  background: var(--background-color3);
  color: var(--text-color1);
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.5;
  max-height: 240px;
  overflow-y: auto;
}

.modal-actions {
  margin-top: 1.25rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: flex-end;
}

button.close {
  background: var(--background-color3);
  color: var(--text-color1);
  border: 1px solid var(--background-color3);
  padding: 0.6rem 1.1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}

button.close:hover {
  border-color: var(--border-color-hover);
  color: var(--border-color-hover);
}

.btn-resolve {
  background: rgba(16, 185, 129, 0.25);
  color: #6ee7b7;
  border: 1px solid rgba(16, 185, 129, 0.45);
  padding: 0.6rem 1.1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}

.btn-resolve:hover {
  background: rgba(16, 185, 129, 0.35);
}

@keyframes popIn {
  from {
    opacity: 0;
    transform: scale(0.92);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
